import { createEffect, createRoot, createSignal, untrack } from 'solid-js';
import { createStore, produce, reconcile } from 'solid-js/store';
import { price as unitPrice } from '~data';
import { ACCOUNT_SHAPE, type Account, type AccountKind, type Holding, type Loan } from '~/data/portfolios';
import { accountHoldings, loans as realLoans, realPortfolio, resolve, type ResolvedHolding } from '~/stores/portfolio-store';
import { sumOrNull } from '~/lib/format';

/**
 * Which loan a sandbox position was bought with — a loan id, not a kind.
 *
 * It was a kind, back when there were exactly two loans and the kind was the
 * identity. Loans are yours now and there can be three HELOCs, so the only thing
 * that tells two of them apart is the id.
 */
export type LoanRef = string;

/**
 * A portfolio you type straight into.
 *
 * The real book is derived: a position is the sum of the buys that made it, so
 * the blotter and the holdings cannot disagree because there is only one of
 * them. That is the right model for a record of what happened and the wrong one
 * for asking what if — nobody planning a portfolio wants to invent a trade date
 * and a commission to find out what $20,000 of HMAX would pay.
 *
 * So here a position *is* the stored thing. No transactions, no ids, no rows.
 *
 * Nothing persists, deliberately. This is scratch paper: it starts empty on
 * every load and is gone when the tab closes. A plan you want to keep is a Plan,
 * which is a different thing with a different lifetime.
 */

/**
 * A position, and what paid for it.
 *
 * Funding is a property of the position rather than a separate list hanging off
 * the loan, because a holding bought with borrowed money is still a holding in
 * an account — it is in your TFSA whichever cash bought it, and it pays the same
 * distributions. What changes is the tax treatment of the interest, and that
 * needs to know both the loan and the account, which is exactly this pairing.
 *
 * Which is also why the key is (symbol, loan) and not symbol: $10,000 of HMAX
 * bought with cash and $20,000 of the same fund bought with a HELOC are one
 * position to a broker and two to the CRA.
 */
export interface SandboxHolding extends Holding {
    /** Which loan paid for it. Absent means cash. */
    loan?: LoanRef;
}

export interface SandboxAccount extends Account {
    holdings: SandboxHolding[];
}

const freshAccounts = (): SandboxAccount[] =>
    ACCOUNT_SHAPE.map((shape) => ({
        // An id is still wanted downstream — `resolve` stamps it onto every
        // resolved holding — but it never reaches a database, so the account
        // kind is identity enough.
        id: `sandbox:${shape.kind}`,
        kind: shape.kind,
        label: shape.label,
        sheltered: shape.sheltered,
        deposit: 0,
        holdings: [],
    }));

/**
 * A snapshot of your real facilities, at zero drawn.
 *
 * Copies rather than the rows themselves: typing a balance in here must not
 * change what you actually owe. It starts from the real list so the scratchpad
 * is about the credit you really have — a sandbox offering a HELOC you do not
 * hold is a plan you cannot act on.
 */
const freshLoans = (): Loan[] => realLoans().map((l) => ({ ...l, balance: 0 }));

const [accounts, setAccounts] = createStore<SandboxAccount[]>(freshAccounts());

/**
 * Accounts asked for but not yet filled.
 *
 * The four exist whether or not you use them — a Canadian holds at most one of
 * each and there is nothing to create — so "add an account" here means "show me
 * this one", not "make it". Holding something is the other way in: a card you
 * have put a fund into is obviously in use and does not need to be asked for
 * again.
 */
const [activated, setActivated] = createSignal<AccountKind[]>([]);

export const isShown = (account: SandboxAccount): boolean => account.holdings.length > 0 || activated().includes(account.kind);

export const activateAccount = (kind: AccountKind): void => {
    setActivated((current) => (current.includes(kind) ? current : [...current, kind]));
};
const [loans, setLoans] = createStore<Loan[]>(freshLoans());

/**
 * Keep the scratchpad's facilities in step with the real ones.
 *
 * The real list arrives asynchronously and can change while this page is open —
 * add a HELOC in Profile and it should be here to play with. New facilities come
 * in at zero drawn; ones you have deleted go; and anything already typed in here
 * is kept, because losing a balance you set because a rate changed elsewhere
 * would be the reactive equivalent of the page reloading under you.
 *
 * In a root because it runs at module scope and nothing else owns it. Untracked
 * on the sandbox's own rows so writing to the store does not re-trigger the
 * effect that wrote it.
 */
createRoot(() => {
    createEffect(() => {
        const real = realLoans();
        const mine = untrack(() => loans.map((l) => ({ ...l })));
        setLoans(
            reconcile(
                real.map((l) => {
                    const existing = mine.find((x) => x.id === l.id);
                    return existing == null ? { ...l, balance: 0 } : { ...l, balance: existing.balance, rate: existing.rate };
                }),
                { key: 'id' },
            ),
        );
    });
});

export { accounts, loans };

const accountAt = (kind: AccountKind): number => accounts.findIndex((a) => a.kind === kind);
const loanAt = (loan: LoanRef): number => loans.findIndex((l) => l.id === loan);

/** Same position: same fund, same money behind it. */
const same = (h: SandboxHolding, symbol: string, loan?: LoanRef): boolean => h.symbol === symbol && (h.loan ?? null) === (loan ?? null);

// --- positions ---------------------------------------------------------------

/**
 * Book cost follows the price, because a sandbox position was bought now.
 *
 * The real book keeps what you actually paid, which is what an adjusted cost
 * base is. Here there is no "actually paid" — the position is hypothetical — so
 * cost is what it would cost today. That makes an unrealised gain of exactly
 * zero, which is correct and is also the point: a plan should show what a
 * portfolio *pays*, not a fictional profit invented by the date you typed it in.
 */
const costOf = (symbol: string, units: number): number => {
    const p = unitPrice(symbol);
    return p == null ? 0 : units * p;
};

/** Add a fund to an account at zero, or leave an existing position alone —
    picking a fund twice should not silently reset the amount already typed. */
export const addPosition = (kind: AccountKind, symbol: string, loan?: LoanRef): void => {
    const at = accountAt(kind);
    if (at === -1) return;
    setAccounts(
        at,
        produce((account) => {
            if (account.holdings.some((h) => same(h, symbol, loan))) return;
            account.holdings.push({ symbol, units: 0, bookCost: 0, ...(loan ? { loan } : {}) });
        }),
    );
};

export const removePosition = (kind: AccountKind, symbol: string, loan?: LoanRef): void => {
    const at = accountAt(kind);
    if (at === -1) return;
    setAccounts(
        at,
        produce((account) => {
            account.holdings = account.holdings.filter((h) => !same(h, symbol, loan));
        }),
    );
};

export const setUnits = (kind: AccountKind, symbol: string, units: number, loan?: LoanRef): void => {
    const at = accountAt(kind);
    if (at === -1) return;
    const clean = Number.isFinite(units) ? Math.max(0, units) : 0;
    setAccounts(
        at,
        produce((account) => {
            const held = account.holdings.find((h) => same(h, symbol, loan));
            if (held == null) return;
            held.units = clean;
            held.bookCost = costOf(symbol, clean);
        }),
    );
};

/**
 * Move a loan-funded position into a different account, keeping its size.
 *
 * The control that does this is the whole reason funding is modelled: the same
 * borrowed dollar in a margin account and in a TFSA are treated completely
 * differently, and the point of being able to change it here is to see that.
 *
 * Merges rather than duplicating if the target already holds the same fund on
 * the same loan — two rows that are the same position would then disagree about
 * how big it is.
 */
export const movePosition = (from: AccountKind, symbol: string, loan: LoanRef | undefined, to: AccountKind): void => {
    if (from === to) return;
    const source = accountAt(from);
    const target = accountAt(to);
    if (source === -1 || target === -1) return;

    const held = accounts[source].holdings.find((h) => same(h, symbol, loan));
    if (held == null) return;
    const units = held.units;

    removePosition(from, symbol, loan);
    setAccounts(
        target,
        produce((account) => {
            const existing = account.holdings.find((h) => same(h, symbol, loan));
            if (existing) existing.units += units;
            else account.holdings.push({ symbol, units, bookCost: 0, ...(loan ? { loan } : {}) });
        }),
    );
    const merged = accounts[target].holdings.find((h) => same(h, symbol, loan));
    if (merged) setUnits(to, symbol, merged.units, loan);
};

/**
 * Set the position by what it is worth rather than by unit count.
 *
 * The more natural way in: nobody plans in units, they plan in dollars. Units
 * are derived and kept as the stored quantity, because everything downstream —
 * income, yield, exposure — is units times something.
 *
 * Fractional units are allowed. A broker would round you down and leave the
 * remainder in cash; this is measuring a strategy, not simulating a fill.
 */
export const setAmount = (kind: AccountKind, symbol: string, amount: number, loan?: LoanRef): void => {
    const p = unitPrice(symbol);
    // An unpriced fund cannot convert dollars to units, and guessing is worse
    // than declining — the row shows the amount box disabled instead.
    if (p == null || p <= 0) return;
    setUnits(kind, symbol, Math.max(0, amount) / p, loan);
};

// --- loans -------------------------------------------------------------------

export const setLoanBalance = (kind: LoanRef, balance: number): void => {
    const at = loanAt(kind);
    if (at !== -1) setLoans(at, 'balance', Number.isFinite(balance) ? Math.max(0, balance) : 0);
};

export const setLoanRate = (kind: LoanRef, rate: number): void => {
    const at = loanAt(kind);
    if (at !== -1) setLoans(at, 'rate', Number.isFinite(rate) ? Math.max(0, rate) : 0);
};

/** Whether there is a real book to copy from. */
export const canCopyFromMain = (): boolean => (realPortfolio()?.accounts.length ?? 0) > 0;

/**
 * Load the real book's positions in, replacing whatever is here.
 *
 * Holdings only — not loans, not deposits. The point is to start from what you
 * actually hold and change it; carrying the borrowing over too would make the
 * first thing you see a copy rather than a question.
 *
 * Positions land as cash-funded regardless of how they were really paid for,
 * because funding here is something you set on the loan cards and copying it in
 * would put attributions on screen that you did not make.
 */
export const copyFromMain = (): void => {
    const source = realPortfolio();
    if (source == null) return;
    setAccounts(
        freshAccounts().map((account) => {
            const from = source.accounts.find((a) => a.kind === account.kind);
            if (from == null) return account;
            return { ...account, holdings: accountHoldings(from).map((h) => ({ symbol: h.symbol, units: h.units, bookCost: costOf(h.symbol, h.units) })) };
        }),
    );
};

export const reset = (): void => {
    setAccounts(freshAccounts());
    setLoans(freshLoans());
    setActivated([]);
};

// --- what it comes to ---------------------------------------------------------

// Resolution is the real book's, not a copy of it. A sandbox position has to be
// worth what the same position is worth in the book, or the whole exercise is
// answering a different question than the one it looks like it answers.
export const resolvedIn = (account: SandboxAccount): ResolvedHolding[] =>
    account.holdings.map((h) => resolve(h, account)).filter((r): r is ResolvedHolding => r !== null);

// Plain functions, not memos. The whole sandbox is four accounts and a handful
// of positions, so there is nothing here worth caching — and a store read inside
// a component tracks either way, so nothing is lost. What is gained is that
// every one of these can be called and checked outside a render, which a memo
// cannot: outside a reactive root it hands back a stale value and a test of it
// quietly passes on nothing.
export const resolvedAll = (): ResolvedHolding[] => accounts.flatMap(resolvedIn);

export const accountValue = (account: SandboxAccount): number | null => sumOrNull(resolvedIn(account).map((r) => r.value));
export const accountIncome = (account: SandboxAccount): number | null => sumOrNull(resolvedIn(account).map((r) => r.income));
/**
 * Interest charged to this account: the borrowed money invested here, at the
 * rate of the loan that supplied it.
 *
 * Deliberately not a share of the whole loan. Money drawn and left idle is
 * charged to no account, because it is not in one — which means these do not sum
 * to `totalInterest`, and should not: the page-level figure is what the loans
 * cost, this is what each account costs to hold.
 */
export const accountInterest = (account: SandboxAccount): number =>
    account.holdings.reduce((sum, h) => {
        if (h.loan == null) return sum;
        const at = loanAt(h.loan);
        const p = unitPrice(h.symbol);
        return at === -1 || p == null ? sum : sum + h.units * p * (loans[at].rate / 100);
    }, 0);

/** What the account actually pays after the cost of any borrowing inside it. */
export const accountNetIncome = (account: SandboxAccount): number | null => {
    const income = accountIncome(account);
    return income == null ? null : income - accountInterest(account);
};

export const accountCost = (account: SandboxAccount): number => resolvedIn(account).reduce((sum, r) => sum + r.holding.bookCost, 0);

export const totalValue = (): number | null => sumOrNull(accounts.map(accountValue));
export const totalIncome = (): number | null => sumOrNull(accounts.map(accountIncome));
export const totalDebt = (): number => loans.reduce((sum, l) => sum + l.balance, 0);

/** Interest at the stated rates, per year — the cost side of a levered plan. */
export const totalInterest = (): number => loans.reduce((sum, l) => sum + (l.balance * l.rate) / 100, 0);

/** Income after the borrowing that funded it. The number the whole page exists
    to put a sign on: positive carry means the position pays for itself. */
export const netIncome = (): number | null => {
    const income = totalIncome();
    return income == null ? null : income - totalInterest();
};

/** Value less what is owed. */
export const equity = (): number | null => {
    const value = totalValue();
    return value == null ? null : value - totalDebt();
};

// --- the loan side -----------------------------------------------------------

/** Every position this loan paid for, with the account it sits in. */
export const loanHoldings = (loan: LoanRef): { account: SandboxAccount; holding: SandboxHolding }[] =>
    accounts.flatMap((account) => account.holdings.filter((h) => h.loan === loan).map((holding) => ({ account, holding })));

/** What the loan has actually been put into, at today's prices. */
export const loanDeployed = (loan: LoanRef): number =>
    loanHoldings(loan).reduce((sum, { holding }) => {
        const p = unitPrice(holding.symbol);
        return sum + (p == null ? 0 : holding.units * p);
    }, 0);

/**
 * Borrowed but not yet placed. Negative means the positions attributed to this
 * loan are worth more than the loan — which is not an error to prevent, since
 * you can attribute a position bought partly with cash, but is worth showing.
 */
export const loanUndeployed = (loan: LoanRef): number => {
    const at = loanAt(loan);
    return at === -1 ? 0 : loans[at].balance - loanDeployed(loan);
};

/** What this loan's positions pay a year — the income side of the carry, for
    the borrowing that bought it rather than for the book as a whole. */
export const loanIncome = (loan: LoanRef): number | null =>
    sumOrNull(loanHoldings(loan).map(({ account, holding }) => resolve(holding, account)?.income ?? null));

/** Interest on one loan. */
export const loanInterest = (loan: LoanRef): number => {
    const at = loanAt(loan);
    return at === -1 ? 0 : (loans[at].balance * loans[at].rate) / 100;
};

/**
 * Whether the interest on this loan looks deductible.
 *
 * Not a tax opinion and not yet used for anything — the rule in Canada turns on
 * the money being borrowed to earn income, so borrowing into a TFSA or an RRSP
 * gets you nothing while the same loan into a margin account is deductible. It
 * is recorded here because the sandbox now knows both halves; what to do with it
 * is a later question.
 */
export const loanIntoSheltered = (loan: LoanRef): number =>
    loanHoldings(loan)
        .filter(({ account }) => account.sheltered)
        .reduce((sum, { holding }) => {
            const p = unitPrice(holding.symbol);
            return sum + (p == null ? 0 : holding.units * p);
        }, 0);

/** Income against what it is invested in — the same measure the book uses. */
export const portfolioYield = (): number | null => {
    const value = totalValue();
    const income = totalIncome();
    return value == null || income == null || value <= 0 ? null : (income / value) * 100;
};

/** Debt as a share of market value — the leverage the loans imply. */
export const leverage = (): number | null => {
    const value = totalValue();
    return value == null || value <= 0 ? null : (totalDebt() / value) * 100;
};

/**
 * The share of a loan's interest that is deductible.
 *
 * Interest is deductible in Canada to the extent the borrowed money was used to
 * earn income, so what qualifies falls out of the account each loan-funded
 * position sits in — sheltering the income shelters the deduction with it. The
 * sandbox knows that directly, since every loan-funded position names its
 * account.
 *
 * Measured against the balance, not against what was placed: money drawn and
 * left idle earned nothing and deducts nothing. Clamped to [0,1] so attributing
 * more than the balance cannot manufacture a deduction larger than the interest.
 */
const deductibleShare = (loan: LoanRef): number => {
    const at = loanAt(loan);
    if (at === -1 || loans[at].balance <= 0) return 0;
    const intoTaxable = loanHoldings(loan)
        .filter(({ account }) => !account.sheltered)
        .reduce((sum, { holding }) => {
            const p = unitPrice(holding.symbol);
            return sum + (p == null ? 0 : holding.units * p);
        }, 0);
    return Math.min(1, Math.max(0, intoTaxable / loans[at].balance));
};

export const deductibleInterest = (): number => loans.reduce((sum, l) => sum + loanInterest(l.id) * deductibleShare(l.id), 0);

/** Weighted average rate across the loans, by balance — the one number that
    stands for "what this borrows at" when it borrows at several rates. */
export const borrowRate = (): number | null => {
    const balance = totalDebt();
    if (balance <= 0) return null;
    return loans.reduce((sum, l) => sum + l.balance * l.rate, 0) / balance;
};

/**
 * What the borrowed money itself earns — income from the positions attributed to
 * the loans, over the amount borrowed.
 *
 * Not the book's yield. The Loans page has to assume borrowed dollars earn the
 * average, because a blotter does not say which trade a loan paid for. The
 * sandbox does say, so assuming would be wrong here in a way you can see: draw
 * $50,000, buy nothing with it, and the average-yield version reports a healthy
 * spread on money sitting in cash.
 */
/** What the positions the borrowing paid for pay, per year. Named once so the
    yield and the carry cannot be built from different sets of loans. */
export const borrowedIncome = (): number => loans.reduce((sum, l) => sum + (loanIncome(l.id) ?? 0), 0);

export const borrowedYield = (): number | null => {
    const debt = totalDebt();
    if (debt <= 0) return null;
    return (borrowedIncome() / debt) * 100;
};

/**
 * Yield on the borrowed money less what it costs.
 *
 * Null with no debt rather than zero. Zero spread is a real state — borrowing at
 * exactly the yield — and it is not the same as not borrowing.
 */
export const spread = (): number | null => {
    const earned = borrowedYield();
    const rate = borrowRate();
    return earned == null || rate == null ? null : earned - rate;
};

/**
 * What the borrowing nets, in dollars per month.
 *
 * The same definition the Loans page uses, deliberately: borrowed money earns at
 * the book's yield and costs its loan rate. Matching it is the point — a sandbox
 * figure computed a second way would not be comparable to the real book's, which
 * is the comparison the whole page is for.
 *
 * Attributed, not averaged — the same basis as each loan's own line, so the card
 * and the loans below it cannot disagree.
 *
 * Pre-tax. Deductibility makes the real figure better, by an amount that depends
 * on a marginal rate this app deliberately does not guess at.
 */
export const carryPerMonth = (): number | null => {
    if (totalDebt() <= 0) return null;
    return (borrowedIncome() - totalInterest()) / 12;
};

export const isEmpty = (): boolean => accounts.every((a) => a.holdings.length === 0) && loans.every((l) => l.balance === 0);
